import React from 'react';
import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  Theme,
  Typography
} from '@mui/material';
import { iconHandler } from '../../utils';
import { ICON_TYPE, colors, fontSize, fontWeight } from '../../constants';

interface MenuItemType {
  label: string;
  value: string | number;
}

type VariantType = 'standard' | 'outlined' | 'contained' | 'borderless' | 'rounded';

interface BasicSelectProps {
  menuItem: MenuItemType[];
  onChange: (event: SelectChangeEvent) => void;
  value: string;
  styled?: boolean;
  name?: string;
  labelText?: string;
  required?: boolean;
  readOnly?: boolean;
  error?: boolean;
  helperText?: string;
  variant?: VariantType;
  fullWidth?: boolean;
}

type StyleComponent = 'formControl' | 'label' | 'select';

const variantStyles = {
  standard: {
    borderRadius: '8px',
    backgroundColor: colors.blueChipBg,
    fieldsetBorderColor: colors.iconSearch,
    fieldsetBorderWidth: '1.5px !important',
    svgMarginRight: '2px',
    fieldsetDisplay: 'flex'
  },
  outlined: {
    borderRadius: '4px',
    backgroundColor: undefined,
    fieldsetBorderColor: colors.grayBorder,
    fieldsetBorderWidth: '2px !important',
    svgMarginRight: '-2px',
    fieldsetDisplay: 'flex'
  },
  contained: {
    borderRadius: '8px',
    backgroundColor: 'transparent',
    fieldsetBorderColor: colors.grayBorder,
    fieldsetBorderWidth: '2px !important',
    svgMarginRight: '2px',
    fieldsetDisplay: 'flex'
  },
  borderless: {
    borderRadius: '0px',
    backgroundColor: 'transparent',
    fieldsetBorderColor: 'transparent',
    fieldsetBorderWidth: '2px !important',
    svgMarginRight: '-4px',
    fieldsetDisplay: 'none'
  },
  rounded: {
    borderRadius: '20px',
    backgroundColor: colors.blueChipBg,
    fieldsetBorderColor: colors.blueBase,
    fieldsetBorderWidth: '2px !important',
    svgMarginRight: '2px !important',
    fieldsetDisplay: 'flex'
  }
} as const;

const getStyle = (
  type: StyleComponent,
  props: {
    styled?: boolean;
    variant?: VariantType;
    fullWidth?: boolean;
    readOnly?: boolean;
  } = {}
): SxProps<Theme> => {
  const { styled, variant = 'standard', fullWidth, readOnly } = props;
  const currentVariant = variantStyles[variant];

  if (type === 'formControl') {
    return {
      minWidth: styled ? 100 : 80,
      width: fullWidth ? '100%' : 'auto',
      borderRadius: currentVariant.borderRadius,
      ...(variant === 'borderless' && {
        border: 'none',
        boxShadow: 'none'
      }),

      ...(variant === 'rounded' && {
        overflow: 'hidden'
      })
    };
  }

  if (type === 'label') {
    return {
      color: colors.blackOlive,
      fontSize: fontSize.small,
      fontWeight: fontWeight.medium
    };
  }

  if (type === 'select') {
    return {
      borderRadius: currentVariant.borderRadius,
      backgroundColor: currentVariant.backgroundColor,
      '& .MuiSelect-select': {
        fontSize: '16px',
        padding: '8px 20px',
        paddingRight: '30px !important',
        color: colors.black,
        '&::placeholder': {
          color: colors.grayLight,
          opacity: 1
        }
      },
      '& svg': {
        cursor: 'pointer',
        position: 'absolute',
        right: currentVariant.svgMarginRight || '-4px',
        width: '20px',
        height: '20px',
        pointerEvents: 'none'
      },
      '& fieldset': {
        borderColor: currentVariant.fieldsetBorderColor,
        display: currentVariant.fieldsetDisplay,
        alignItems: 'center'
      },

      '&:hover fieldset': {
        borderColor: `${colors.iconSearch} !important`,
        borderWidth: '1px !important'
      },
      '&.Mui-focused fieldset': {
        borderColor: currentVariant.fieldsetBorderColor,
        borderWidth: currentVariant.fieldsetBorderWidth,
        display: currentVariant.fieldsetDisplay,
        alignItems: 'center'
      },
      '& ~ .MuiFormHelperText-root': {
        margin: 0,
        marginTop: '1px !important'
      },

      ...(readOnly && {
        pointerEvents: 'none'
      })
    };
  }

  return {};
};

const BasicSelect: React.FC<BasicSelectProps> = ({
  menuItem,
  onChange,
  value,
  styled = false,
  name = '',
  labelText,
  required,
  readOnly = false,
  error = false,
  helperText,
  variant = 'standard',
  fullWidth = false,
  ...rest
}) => {
  const ArrowDownIcon = () => iconHandler(ICON_TYPE.ARROW_DOWN, colors.black, 1.2, 30);

  return (
    <>
      {labelText && (
        <Typography sx={getStyle('label')}>
          {labelText}
          {required && (
            <Typography component="span" color="error">
              {' '}
              *
            </Typography>
          )}
        </Typography>
      )}
      <FormControl
        sx={getStyle('formControl', { styled, variant, fullWidth })}
        error={error}
        fullWidth={fullWidth}>
        <Select
          name={name}
          value={value}
          fullWidth={fullWidth}
          onChange={onChange}
          displayEmpty
          MenuProps={{ disableScrollLock: true }}
          IconComponent={ArrowDownIcon}
          readOnly={readOnly}
          variant="outlined"
          sx={getStyle('select', { variant, readOnly })}
          {...rest}>
          {menuItem.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
        {helperText && (
          <Typography variant="caption" color="error">
            {helperText}
          </Typography>
        )}
      </FormControl>
    </>
  );
};

export default BasicSelect;
